import { z } from "zod";

/**
 * Party Bookings (Phase 2d) — mirrors app/schemas/party.py's shapes.
 */
import { BookingStatusSchema } from "./booking";

export const PartyOccasionSchema = z.enum(["birthday", "holiday", "graduation", "other"]);
export type PartyOccasion = z.infer<typeof PartyOccasionSchema>;

export const PARTY_OCCASION_LABELS: Record<PartyOccasion, string> = {
  birthday: "Birthday",
  holiday: "Holiday",
  graduation: "Graduation",
  other: "Other",
};

// Zone A additions: `station`/`project_item` — one PartyAddon row per
// selected catalog item (see api/app/models/party_activity_catalog.py).
// The original three values are unchanged.
export const PartyAddonTypeSchema = z.enum([
  "art_project",
  "art_project_premium",
  "china_setting",
  "station",
  "project_item",
]);
export type PartyAddonType = z.infer<typeof PartyAddonTypeSchema>;

export const PARTY_ADDON_LABELS: Record<PartyAddonType, string> = {
  art_project: "Art Project Package",
  art_project_premium: "Art Project Package (Premium)",
  china_setting: "China Place Setting",
  station: "Station",
  project_item: "Project",
};

// Kept in sync with app/services/party_pricing.py — display-only preview
// constants; the server is always the source of truth for the actual
// snapshotted unit_price_cents on each PartyAddon row. station/project_item
// have no fixed unit price here (station is always $0; a project's price
// depends on its own catalog row + headcount) — see partyMath.ts for the
// selection-pricing preview helpers.
export const PARTY_ADDON_UNIT_PRICE_CENTS: Record<PartyAddonType, number> = {
  art_project: 2_000,
  art_project_premium: 2_500,
  china_setting: 1_000,
  station: 0,
  project_item: 0,
};

export const PartyAddonSourceSchema = z.enum(["booking", "portal"]);
export type PartyAddonSource = z.infer<typeof PartyAddonSourceSchema>;

export const PartyAddonSchema = z.object({
  id: z.string(),
  addon_type: PartyAddonTypeSchema,
  quantity: z.number(),
  unit_price_cents: z.number(),
  total_cents: z.number(),
  source: PartyAddonSourceSchema,
  created_at: z.string(),
  // Zone A — set only for addon_type in (station, project_item).
  catalog_item_id: z.string().nullable(),
  catalog_item_name: z.string().nullable(),
});
export type PartyAddon = z.infer<typeof PartyAddonSchema>;

// Zone A: the two party formats — mirrors app/models/party_booking.py::PartyFormat.
export const PartyFormatSchema = z.enum(["stations", "project_based"]);
export type PartyFormat = z.infer<typeof PartyFormatSchema>;

export const PARTY_FORMAT_LABELS: Record<PartyFormat, string> = {
  stations: "Stations",
  project_based: "Project-Based",
};

// Client picks exactly this many selections for each format — mirrors
// app/schemas/party.py's _SELECTION_COUNT_BY_FORMAT.
export const PARTY_SELECTION_COUNT_BY_FORMAT: Record<PartyFormat, number> = {
  stations: 3,
  project_based: 10,
};

// Zone A: the party activity catalog — mirrors
// app/schemas/party.py::PartyActivityCatalogOut.
export const PartyActivityCatalogItemSchema = z.object({
  id: z.string(),
  format: PartyFormatSchema,
  name: z.string(),
  tier: z.string().nullable(),
  base_price_cents: z.number(),
  premium_per_kid_surcharge_cents: z.number(),
  sort_order: z.number(),
});
export type PartyActivityCatalogItem = z.infer<typeof PartyActivityCatalogItemSchema>;

export const PartyBookingSchema = z.object({
  id: z.string(),
  booking_id: z.string(),
  resource_id: z.string(),
  resource_name: z.string(),
  slot_id: z.string().nullable(),
  slot_label: z.string().nullable(),
  booking_date: z.string(),
  extra_hour: z.boolean(),
  status: BookingStatusSchema,
  contact_id: z.string().nullable(),
  headcount: z.number().nullable(),
  occasion: PartyOccasionSchema,
  child_age: z.number().nullable(),
  notes: z.string().nullable(),
  format: PartyFormatSchema,
  base_price_cents: z.number(),
  addons: z.array(PartyAddonSchema),
  total_price_cents: z.number(),
  suggested_addons: z.array(PartyAddonTypeSchema),
  invoice_id: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string().nullable(),
});
export type PartyBooking = z.infer<typeof PartyBookingSchema>;

export const PartyBookingListResponseSchema = z.object({
  items: z.array(PartyBookingSchema),
  next_cursor: z.string().nullable(),
});
export type PartyBookingListResponse = z.infer<typeof PartyBookingListResponseSchema>;

export const WeekendSlotSummarySchema = z.object({
  weekend_start: z.string(),
  booked: z.number(),
  remaining: z.number(),
  max_slots: z.number(),
});
export type WeekendSlotSummary = z.infer<typeof WeekendSlotSummarySchema>;

export const WeekendSlotSummaryResponseSchema = z.object({ items: z.array(WeekendSlotSummarySchema) });

// ---- Mutation inputs --------------------------------------------------------

export interface PartyAddonRequestInput {
  addon_type: PartyAddonType;
  quantity: number;
}

export interface CreatePartyInput {
  resource_id: string;
  slot_id: string;
  booking_date: string;
  extra_hour?: boolean;
  status?: string;
  contact_id?: string | null;
  headcount?: number | null;
  occasion?: PartyOccasion;
  child_age?: number | null;
  notes?: string | null;
  addons?: PartyAddonRequestInput[];
  // Zone A — required by the backend schema (app/schemas/party.py::
  // PartyBookingCreate); no default here either, matching that "required
  // over defaulted where correctness matters" rule.
  format: PartyFormat;
  selections: string[];
}
